/* eslint-disable anti-slop/require-safety-comment-for-type-assertion, anti-slop/no-object-parameters, anti-slop/no-known-value-widening -- verbatim restore of the pre-FA-3036 data dashboard, served while data_dashboard_v2_enabled is off */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import { Box, Grid, ListItemText, MenuItem, ThemeProvider, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import {
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHButton,
  FCheckbox,
  FHSelect,
  FMultiSelect,
  FProgressBar,
} from '../components/base';
import {
  clientActions,
  goalActions,
  sessionActions,
  treatmentPlanActions,
  treatmentRecommendationActions,
} from '../store';
import {
  areUserRolesIn,
  capitalizeFirstLetter,
  disabledFutureDate,
  formatEntityId,
} from '../helpers';
import { dataTableTheme } from '../utils/theme';
import PageContainer from '../components/layout/PageContainer';
import { BackgroundTypes, Roles, Themes } from '../constants';
import DataChartLegacy from '../components/layout/data-dashboard/DataChartLegacy';
import TargetPhaseChangeTableLegacy from '../components/layout/data-dashboard/TargetPhaseChangeTableLegacy';
import {
  calculateDurationTrialSummaryData,
  calculateTrialAverage,
  formatDataDashboardDate,
} from './utils';
import { FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../utils/colors';
import { linearRegression } from '../helpers/utility-functions';
import { DURATION_PREFIX } from '../components/layout/data-dashboard/constants';
import {
  DurationEventMeasurementUnitEnum,
  RateMethodMeasurementUnitsIntervalUnitEnum,
  TPGoalDataCollectionMethod,
} from '../components/layout/treatment-plan/goals/common/constants/general';
import {
  DurationChartPlotMethod,
  DurationChartPlotOptions,
  TARGET_STAGE_LOG_PHASE_CHANGE_TYPE,
} from './constants';

import type { GoalTypes } from '../components/layout/treatment-plan/goals/common/constants/general';
import type { ITrialAverageTableData } from '../utils/types';
import type {
  IClient,
  IDashboardTarget,
  IDashboardTargetDay,
  IOption,
  ITreatmentPlanGoal,
  ITargetPhaseChange,
  IDDClientGoals,
  ITrialSummary,
  ITrialSummaryTarget,
  SelectValueType,
  IDurationTrialAverageTableData,
} from '../utils/types';
import type { RootState } from '../store';
import type { SelectChangeEvent } from '@mui/material';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';

interface ITargetOption {
  id?: number;
  label: string;
  checked: boolean;
}

interface IChartDataObject {
  [key: number]: { [key: string]: number | string };
}

const H = 'h';
const M = 'm';
const S = 's';

const getDurationSuffix = (duration_unit: string) =>
  duration_unit === 'HOUR' ? H : duration_unit === 'MINUTE' ? M : S;

const DURATION_MAP: { [key: string]: string } = {
  [H]: 'hours',
  [M]: 'minutes',
  [S]: 'seconds',
};

function DataDashboardLegacy() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { list: activeTPGoals } = useSelector((state: RootState) => state.goals);
  const { user } = useSelector((state: RootState) => state.auth);
  const { list: clients = [], loading: clientsLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const { trialSummary, loading: sessionLoading } = useSelector(
    (state: RootState) => state.sessions,
  );
  const {
    list: treatmentPlansOfClient,
    activeTreatmentPlan,
    goalsOfAllConcludedTreatmentPlans: goals,
    loading: tpLoading,
  } = useSelector((state: RootState) => state.treatmentPlans);
  const { recommendation } = useSelector((state: RootState) => state.treatmentRecommendations);

  const isBTView = areUserRolesIn(user?.roles, [Roles.Prbt]);

  const [showNoDataAlert, setShowNoDataAlert] = useState<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [clientGoals, setClientGoals] = useState<IDDClientGoals[]>([]);
  const [goalTargetsTimeseriesData, setGoalTargetsTimeseriesData] = useState<IDashboardTarget[]>(
    [],
  );
  const [isQueryChanged, setIsQueryChanged] = useState<boolean>(true);
  const [isQueryValid, setIsQueryValid] = useState<boolean>(true);
  const [durationChartPlotMethod, setDurationChartPlotMethod] = useState<DurationChartPlotMethod>(
    () => DurationChartPlotMethod.AVERAGE,
  );
  const [goalTargetsPhaseChanges, setGoalTargetsPhaseChanges] = useState<Array<ITargetPhaseChange>>(
    [],
  );
  const [filteredPhaseChanges, setfilteredPhaseChanges] = useState<Array<ITargetPhaseChange>>([]);
  const [phaseChangeTableData, setPhaseChangeTableData] = useState<
    Array<{ [key: string]: string }>
  >([]);
  const [trialAverageTableData, setTrialAverageTableData] = useState<ITrialAverageTableData[]>([]);
  const [durationTrialSummaryTableData, setDurationTrialSummaryTableData] = useState<
    IDurationTrialAverageTableData[]
  >([]);
  const [selectedGoal, setSelectedGoal] = useState<IDDClientGoals | null>(null);
  const [selectedTargets, setSelectedTargets] = useState<IDashboardTarget[]>([]);
  const [targetOptions, setTargetOptions] = useState<ITargetOption[]>([]);
  const [showBaseline, setShowBaseline] = useState(true);
  const [chartData, setChartData] = useState<{ [key: string]: string }[]>([]);
  const [tableData, setTableData] = useState<string[][]>([]);
  const [dcm, setDcm] = useState('');
  const [durationUnit, setDurationUnit] = useState('');
  const [rateUnit, setRateUnit] = useState('');
  const [tableColumnNames, setTableColumnNames] = useState<any[]>([]);

  const chartEndDate = useMemo(() => {
    return isBTView && chartData.length
      ? moment
          .unix(+chartData[chartData.length - 1].date)
          .add(5, 'days')
          .format('MM/DD/YY')
      : endDate;
  }, [isBTView, chartData, endDate]);

  const chartStartDate = useMemo(() => {
    return isBTView && chartData.length
      ? moment.unix(+chartData[0].date).subtract(5, 'days').format('MM/DD/YY')
      : startDate;
  }, [isBTView, chartData, startDate]);

  useEffect(() => {
    dispatch(clientActions.getList({ getAccessTokenSilently }));
    dispatch(treatmentPlanActions.getTreatmentPlanStatuses({ getAccessTokenSilently }));
    return () => {
      dispatch(clientActions.clearSelectedClient());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!recommendation && !isBTView) return;
    // for BT view set current date as end date and start date as current date - 364 days
    setStartDate(moment().subtract(364, 'days').format('MM/DD/YY'));
    setEndDate(moment().format('MM/DD/YY'));
  }, [recommendation, isBTView]);

  useEffect(() => {
    let isValid = Boolean(
      selectedClient && selectedGoal && targetOptions.filter((t) => t.checked).length,
    );
    if (!isBTView) {
      isValid = isValid && Boolean(startDate && endDate);
    }
    setIsQueryValid(isValid);
  }, [isBTView, startDate, endDate, selectedClient, selectedGoal, targetOptions]);

  useEffect(() => {
    setIsQueryChanged(true);
    setShowNoDataAlert(false);
    setGoalTargetsTimeseriesData([]);
  }, [startDate, endDate, selectedClient, selectedGoal]);

  useEffect(() => {
    setIsQueryChanged(true);
    setShowNoDataAlert(false);
  }, [targetOptions]);

  useEffect(() => {
    if (!treatmentPlansOfClient || treatmentPlansOfClient.length === 0) return;
    if (isBTView) {
      if (!activeTreatmentPlan) {
        if (selectedClient) {
          toast.error("Client doesn't have an active TP");
        }
        return;
      }
      dispatch(
        goalActions.getGoals({ getAccessTokenSilently, treatmentPlanId: activeTreatmentPlan.id }),
      );
      dispatch(
        treatmentRecommendationActions.getRecommendation({
          treatmentPlanId: activeTreatmentPlan.id,
          getAccessTokenSilently,
          forceUpdate: true,
        }),
      );
    } else {
      dispatch(
        treatmentPlanActions.getGoalsOfAllConcludedTreatmentPlans({
          getAccessTokenSilently,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlansOfClient, activeTreatmentPlan, isBTView]);

  useEffect(() => {
    if (!activeTPGoals?.length || !isBTView) return;
    setClientGoals(
      activeTPGoals.map((goal: ITreatmentPlanGoal) => ({
        id: goal?.id as number,
        label: goal?.goal_name?.label as string,
        goal_type: goal?.goal_type as GoalTypes,
        targets: goal?.targets,
      })),
    );
  }, [activeTPGoals, isBTView]);

  useEffect(() => {
    const selected = goalTargetsTimeseriesData.filter((target) =>
      Boolean(targetOptions.find((t: ITargetOption) => t.checked && t.id === target.id)),
    );
    setSelectedTargets(selected);
    if (targetOptions.filter((t) => t.checked).length > 0 && goalTargetsTimeseriesData.length) {
      const isDataPresent = goalTargetsTimeseriesData.some((goal) => {
        return Boolean(targetOptions.find((option) => goal.id === option.id && option.checked));
      });
      if (!isDataPresent) {
        setShowNoDataAlert(true);
      }
    }
  }, [goalTargetsTimeseriesData, targetOptions]);

  useEffect(() => {
    if (!goals || goals.length === 0 || isBTView) return;
    setClientGoals(
      goals.map((goal: ITreatmentPlanGoal) => ({
        id: goal?.id as number,
        label: goal?.goal_name?.label as string,
        goal_type: goal?.goal_type as GoalTypes,
        targets: goal?.targets,
        treatment_plan_id: goal?.treatment_plan_id as number,
      })),
    );
  }, [goals, isBTView]);

  // clear targets when client is changed
  useEffect(() => {
    if (!selectedClient || !selectedClient?.id) {
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      return;
    }
    dispatch(
      treatmentPlanActions.getTreatmentPlanByQuery({
        query: { clientId: selectedClient.id },
        getAccessTokenSilently,
      }),
    );
    setSelectedGoal(null);
    setClientGoals([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  // set target options on goal select
  useEffect(() => {
    const options: ITargetOption[] = [];
    selectedGoal?.targets.forEach((t) => {
      if (t.id) {
        options.push({
          id: t.id,
          label: t.label,
          checked: false,
        });
      }
    });
    if (options.length === 1) {
      options[0].checked = true;
    }
    setTargetOptions(options);
  }, [selectedGoal]);

  useEffect(() => {
    if (!trialSummary) return;
    if (
      trialSummary &&
      trialSummary.length === 0 &&
      targetOptions.filter((t) => t.checked).length
    ) {
      setShowNoDataAlert(true);
    }

    const selectedGoalTrialSummaryData: ITrialSummary = trialSummary.find(
      (targetData: ITrialSummary) => targetData.goal_id === selectedGoal?.id,
    );

    const selectedGoalTargetData: ITrialSummaryTarget[] =
      selectedGoalTrialSummaryData?.targets ?? [];

    let dcm = '';
    let durationUnit = '';
    let rateUnit = '';

    const goalTrials = selectedGoalTargetData.find(({ trials }) => trials.length > 0)?.trials ?? [];

    if (goalTrials.length) {
      dcm = goalTrials[0]?.data_collection_method ?? '';
      durationUnit = goalTrials[0]?.measurement_units_duration_unit ?? '';
      rateUnit = goalTrials[0]?.measurement_units_interval_unit ?? '';
    }

    setDcm(dcm);
    setDurationUnit(durationUnit);
    setRateUnit(rateUnit);

    const goalTargetsPhaseChangesData: ITargetPhaseChange[] = [];
    const targetsOfSelectedGoal = selectedGoalTargetData?.map((targetData: ITrialSummaryTarget) => {
      if (targetData.phase_changes?.length) {
        const targetPhaseChanges = targetData.phase_changes?.map((phase: ITargetPhaseChange) => {
          return {
            ...phase,
            date_timestamp: moment(phase.date_added).unix(),
            target_id: targetData.target_id,
            line_label: moment(phase.date_added).format('MM/DD'),
          };
        });
        goalTargetsPhaseChangesData.push(...targetPhaseChanges);
      }

      // add phase change from goal data
      const targetDataFromGoal = selectedGoal?.targets?.find(
        (target) => target.id === targetData.target_id,
      );
      if (targetDataFromGoal) {
        const phaseDataFromGoal =
          targetDataFromGoal.stage_change_logs?.map((log) => ({
            date_added: log.date_stage_changed,
            id: log.id,
            title: `${log.old_stage ? `${log.old_stage} to ` : ''} ${log.new_stage} (${
              log.reason_for_change
            })`,
            date_timestamp: moment(log.date_stage_changed).unix(),
            target_id: targetData.target_id,
            line_label: moment(log.date_stage_changed).format('MM/DD'),
            type: TARGET_STAGE_LOG_PHASE_CHANGE_TYPE,
          })) ?? [];
        goalTargetsPhaseChangesData.push(...phaseDataFromGoal);
      }

      const trialDataByDate = _.groupBy(targetData.trials, (trial: IDashboardTargetDay) =>
        formatDataDashboardDate(trial.date),
      );
      const consolidatedTrialData = Object.entries(trialDataByDate).map((datum) => {
        const trialDate = datum[0];
        const trialsOfDate = datum[1];
        const sumAttempts = trialsOfDate.reduce((sum, trial) => {
          return sum + trial.attempts;
        }, 0);

        // Calculate data for rate
        let rateTotal = 0;
        let sessionDurationTotal = 0;
        let sessionDurationTotalUnit = 1;
        let rateTimeUnit = '';

        trialsOfDate.forEach((trial) => {
          rateTotal += trial?.total_trial_rate_events ?? 0;
          sessionDurationTotal += trial?.expected_duration ?? 0;
          rateTimeUnit = trial?.measurement_units_interval_unit ?? '';
        });
        if (rateTimeUnit) {
          switch (rateTimeUnit) {
            case RateMethodMeasurementUnitsIntervalUnitEnum.HOUR:
              sessionDurationTotalUnit = sessionDurationTotal / 3600;
              break;
            case RateMethodMeasurementUnitsIntervalUnitEnum.MINUTE:
              sessionDurationTotalUnit = sessionDurationTotal / 60;
              break;
            case RateMethodMeasurementUnitsIntervalUnitEnum.SECOND:
              sessionDurationTotalUnit = sessionDurationTotal;
          }
        }
        const rate = parseFloat((rateTotal / sessionDurationTotalUnit).toFixed(2));

        // Calculate data for durations
        let durationEventTotal = 0;
        let durationEventAverage = 0;
        let durationEventCount = 0;
        let durationUnitConvertNum = 1;
        let durationTimeUnit = '';

        trialsOfDate.forEach((trial) => {
          durationEventTotal += trial?.duration_events?.reduce((sum, dur) => sum + dur, 0) ?? 0;
          durationEventCount += trial?.duration_events?.length ?? 0;
          durationTimeUnit = trial?.measurement_units_duration_unit ?? '';
        });

        if (durationTimeUnit) {
          switch (durationTimeUnit) {
            case DurationEventMeasurementUnitEnum.hour:
              durationUnitConvertNum = 3600;
              break;
            case DurationEventMeasurementUnitEnum.minute:
              durationUnitConvertNum = 60;
              break;
            case DurationEventMeasurementUnitEnum.second:
              durationUnitConvertNum = 1;
          }
        }

        durationEventAverage = parseFloat(
          (durationEventTotal / durationUnitConvertNum / durationEventCount).toFixed(2),
        );
        durationEventTotal = parseFloat((durationEventTotal / durationUnitConvertNum).toFixed(2));

        // Calculate data for percentage
        const sumSuccess = trialsOfDate.reduce((sum, trial) => {
          return sum + trial.success;
        }, 0);
        const sumInstances = trialsOfDate.reduce((sum, trial) => {
          return sum + trial.instances;
        }, 0);
        const durationArrays: number[][] = [];
        let total_duration = 0;
        trialsOfDate.forEach((trial) => {
          if (trial.durations) {
            durationArrays.push(trial.durations);
          }
          if (trial.total_duration) {
            total_duration += trial.total_duration;
          }
        });
        const durations = durationArrays.flat().sort((a: number, b: number) => a - b);
        return {
          date: trialDate,
          attempts: sumAttempts,
          success: sumSuccess,
          instances: sumInstances,
          type: trialsOfDate[0].data_collection_method as string,
          durations,
          rate,
          total_duration,
          durationEventTotal,
          durationEventAverage,
          percentage: sumAttempts ? +((100 * sumSuccess) / sumAttempts).toFixed(2) : 0,
          target_label: targetData.label,
          target_id: targetData.target_id,
        };
      });

      return {
        id: targetData?.target_id,
        label: targetData?.label,
        timeSeriesData: consolidatedTrialData,
        type: targetData?.trials[0]?.data_collection_method as string,
        event_duration_measurement_unit: targetData?.event_duration_measurement_unit ?? '',
      };
    });
    setGoalTargetsPhaseChanges(goalTargetsPhaseChangesData);
    setGoalTargetsTimeseriesData(targetsOfSelectedGoal);
    setIsQueryChanged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialSummary]);

  useEffect(() => {
    if (!selectedTargets) return;
    // Calculate trial averages for all selected targets
    const averages = selectedTargets.map((currentTarget) => {
      let durationUnit = 's';
      if (currentTarget?.event_duration_measurement_unit) {
        durationUnit = getDurationSuffix(currentTarget.event_duration_measurement_unit);
      }

      const trialAverage = calculateTrialAverage(currentTarget.timeSeriesData);

      return {
        target: currentTarget.label,
        label: trialAverage.label,
        value: String(trialAverage.value),
        unit: trialAverage.label.toLowerCase().includes('percentage')
          ? '%'
          : trialAverage.label.toLowerCase().includes('duration')
            ? DURATION_MAP[durationUnit]
            : currentTarget.type === TPGoalDataCollectionMethod.RATE ||
                currentTarget.type === TPGoalDataCollectionMethod.DURATION
              ? ''
              : 'instances',
      };
    });

    setTrialAverageTableData(averages);

    if (dcm === TPGoalDataCollectionMethod.DURATION) {
      const durationSummaryTableData = selectedTargets.map((currentTarget) => {
        const trialAverage = calculateDurationTrialSummaryData(currentTarget.timeSeriesData);

        return {
          target: currentTarget.label,
          label: trialAverage.label,
          average: String(trialAverage.average),
          total: trialAverage.total,
        };
      });
      setDurationTrialSummaryTableData(durationSummaryTableData);
    }

    let phaseChangeData: ITargetPhaseChange[] = [];
    if (selectedTargets.length === 1) {
      const currentTarget = selectedTargets[0];
      let durationUnit = 's';
      if (currentTarget?.event_duration_measurement_unit) {
        durationUnit = getDurationSuffix(currentTarget.event_duration_measurement_unit);
      }
      phaseChangeData = handlePhaseChangeDataUpdate(currentTarget.id);
      const targetTableData =
        currentTarget.type === TPGoalDataCollectionMethod.PERCENTAGE
          ? currentTarget?.timeSeriesData
              ?.sort((a, b) => moment(a.date).unix() - moment(b.date).unix())
              ?.map((datum: IDashboardTargetDay) => [
                String(datum.date),
                datum.success + '/' + datum.attempts,
                String(datum.percentage),
              ])
          : currentTarget.type === TPGoalDataCollectionMethod.RATE
            ? currentTarget?.timeSeriesData
                ?.sort((a, b) => moment(a.date).unix() - moment(b.date).unix())
                ?.map((datum: IDashboardTargetDay) => [String(datum.date), String(datum.rate)])
            : currentTarget.type === TPGoalDataCollectionMethod.DURATION
              ? currentTarget?.timeSeriesData
                  ?.sort((a, b) => moment(a.date).unix() - moment(b.date).unix())
                  ?.map((datum: IDashboardTargetDay) => [
                    String(datum.date),
                    String(datum.durationEventAverage),
                    String(datum.durationEventTotal),
                  ])
              : currentTarget?.timeSeriesData
                  ?.sort((a, b) => moment(a.date).unix() - moment(b.date).unix())
                  ?.map((datum: IDashboardTargetDay) => [
                    String(datum.date),
                    String(datum.instances),
                    datum.durations?.length
                      ? `${datum.durations?.join(`, `)} (${datum.total_duration})`
                      : '',
                  ]);
      const showColumnDuration = currentTarget?.timeSeriesData?.some(
        (item) => item.durations?.length,
      );
      setTableData(targetTableData);
      setTableColumnNames(
        currentTarget.type === TPGoalDataCollectionMethod.PERCENTAGE
          ? ['Date', 'Successes', '%']
          : currentTarget.type === TPGoalDataCollectionMethod.RATE
            ? ['Date', `Rate`]
            : currentTarget.type === TPGoalDataCollectionMethod.DURATION
              ? ['Date', 'Average', 'Total']
              : [
                  'Date',
                  'Instances',
                  {
                    name: 'Duration',
                    label: `Duration (in ${DURATION_MAP[durationUnit]})`,
                    options: {
                      display: showColumnDuration,
                    },
                  },
                ],
      );
    } else {
      setPhaseChangeTableData([]);
      setfilteredPhaseChanges([]);
    }
    const allChartDataObject: IChartDataObject = {};
    selectedTargets.forEach((selectedTarget: IDashboardTarget) => {
      const { event_duration_measurement_unit } = selectedTarget;
      let durationUnit = 's';
      if (event_duration_measurement_unit) {
        durationUnit = getDurationSuffix(event_duration_measurement_unit);
      }
      selectedTarget?.timeSeriesData?.forEach((dayData: IDashboardTargetDay) => {
        const currentDate = moment(dayData.date).unix();
        if (allChartDataObject[currentDate]) {
          allChartDataObject[currentDate][dayData?.target_label] =
            dayData?.type === TPGoalDataCollectionMethod.PERCENTAGE
              ? dayData?.percentage
              : dayData?.type === TPGoalDataCollectionMethod.RATE
                ? (dayData?.rate as number)
                : dayData?.type === TPGoalDataCollectionMethod.DURATION
                  ? ((durationChartPlotMethod === DurationChartPlotMethod.AVERAGE
                      ? dayData?.durationEventAverage
                      : dayData?.durationEventTotal) as number)
                  : dayData?.instances;
        } else {
          allChartDataObject[currentDate] = {};
          allChartDataObject[currentDate][dayData?.target_label] =
            dayData?.type === TPGoalDataCollectionMethod.PERCENTAGE
              ? dayData?.percentage
              : dayData?.type === TPGoalDataCollectionMethod.RATE
                ? (dayData?.rate as number)
                : dayData?.type === TPGoalDataCollectionMethod.DURATION
                  ? ((durationChartPlotMethod === DurationChartPlotMethod.AVERAGE
                      ? dayData?.durationEventAverage
                      : dayData?.durationEventTotal) as number)
                  : dayData?.instances;
        }
        // Add durtion values
        if (dayData.durations?.length) {
          allChartDataObject[currentDate][`${DURATION_PREFIX}${dayData?.target_label}`] =
            `${dayData.durations.join(`, `)}${` -- (${dayData.total_duration}${durationUnit})`}`;
        }
      });
    });
    const allChartDataArray = Object.entries(allChartDataObject).map((datum) => {
      const date = datum[0];
      const dayData: { [key: string]: number } = datum[1];
      return {
        date,
        ...dayData,
      };
    });

    let maxYAxisValue = 0;
    allChartDataArray.forEach((datum) => {
      for (const key in datum) {
        if (key !== 'date') {
          maxYAxisValue = Math.max(maxYAxisValue, +datum[key as 'date']);
        }
      }
    });

    const phaseChangeChartData: { [key: string]: string }[] = [];

    phaseChangeData.forEach((item) => {
      if (item.date_timestamp) {
        const startData = {
          date: String(item.date_timestamp),
          [item.title]: '0',
        };
        const endData = {
          date: String(item?.date_timestamp + 10),
          [item.title]: `${maxYAxisValue + 5}`,
        };
        phaseChangeChartData.push(startData, endData);
      }
    });

    let completeChartData = phaseChangeChartData
      ? [...allChartDataArray, ...phaseChangeChartData]
      : allChartDataArray;

    const trialSummaryGoalData = trialSummary?.find((ts) => ts.goal_id === selectedGoal?.id);
    if (trialSummaryGoalData) {
      const baseLineData = {
        Baseline: `${trialSummaryGoalData.baseline_at_creation}`,
        date: `${moment(trialSummaryGoalData.date_introduced).unix()}`,
      };
      completeChartData = [...completeChartData, baseLineData];
    }
    // Add trend line data if single target selected
    if (selectedTargets.length === 1) {
      const target = selectedTargets[0];
      const targetLabel = target.label;

      // Prepare data points for trend line calculation
      const dataPoints = allChartDataArray
        .filter((d) => d[targetLabel as keyof typeof d] !== undefined)
        .map((point) => ({
          x: Number(point.date),
          y: Number(point[targetLabel as keyof typeof point]),
        }))
        .filter((point) => !isNaN(point.y));

      if (dataPoints.length > 1) {
        const { slope, intercept } = linearRegression(dataPoints);

        // Create start and end trend points
        const firstPoint = {
          date: dataPoints[0].x.toString(),
          [`${targetLabel}_trend`]: slope * dataPoints[0].x + intercept,
        };

        const lastPoint = {
          date: dataPoints[dataPoints.length - 1].x.toString(),
          [`${targetLabel}_trend`]: slope * dataPoints[dataPoints.length - 1].x + intercept,
        };

        // Add all trend points to chart data
        completeChartData.push(firstPoint, lastPoint);
      }
    }

    setChartData(completeChartData.sort((a, b) => moment(+a.date).unix() - moment(+b.date).unix()));
    if (moment(trialSummaryGoalData?.date_introduced).isBefore(startDate)) {
      setShowBaseline(false);
    } else {
      setShowBaseline(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTargets, durationChartPlotMethod]);

  const handlePhaseChangeDataUpdate = (selectedTargetId: number) => {
    const phaseChangeTableRows: Array<{ [key: string]: string }> = [];
    const filteredPhaseData = goalTargetsPhaseChanges
      ?.filter((phase: ITargetPhaseChange) => phase.target_id === selectedTargetId)
      .sort((a: ITargetPhaseChange, b: ITargetPhaseChange) => {
        if (moment(a.date_added).isAfter(b.date_added)) {
          return 1;
        } else {
          return -1;
        }
      });

    filteredPhaseData.forEach((phase) => {
      phaseChangeTableRows.push({
        date: moment(phase.date_added).format('MM/DD/YY'),
        title: phase.title,
      });
    });

    setPhaseChangeTableData(phaseChangeTableRows);
    setfilteredPhaseChanges(filteredPhaseData);
    return filteredPhaseData;
  };

  const handleGenerateChartButtonClick = () => {
    const params = {
      client_id: selectedClient?.id,
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      goal_id: selectedGoal?.id,
    };
    dispatch(sessionActions.getTrialSummary({ params, getAccessTokenSilently }));
  };

  return (
    <ThemeProvider theme={dataTableTheme}>
      <PageContainer
        dataTestId="data-dashboard-container"
        sx={{
          px: {
            xs: 2,
            sm: 3,
            md: 3,
            lg: 4,
          },
        }}
      >
        {(clientsLoading || tpLoading || sessionLoading) && <FProgressBar />}
        <Box mt={3} mb={5}>
          <Grid container spacing={2} textAlign="left">
            <Grid item xs={12} sm={6} md={2} sx={{ mr: 2, mb: 1 }}>
              <Typography fontWeight={'bold'} mb={1}>
                Client name:
              </Typography>
              <FHAutocomplete
                data-testid="data-dashboard-client"
                sx={{ width: '100%', boxShadow: 'none' }}
                value={selectedClient}
                options={clients.map((client: IClient) => ({
                  id: client.id,
                  label: `${client.first_name} ${client.last_name}`,
                }))}
                placeholder="Select client"
                clearIcon={false}
                onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                  setSelectedClient(newValue);
                }}
                renderOption={(props: object, option: IOption) => {
                  return (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  );
                }}
                isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
                colorTheme={Themes.Primary}
                coloredBackground
                inputHeight={40}
              />
            </Grid>
            {!isBTView && user && (
              <Grid item xs={12} sm={6} md={1.6} sx={{ mr: 2, mb: 1 }}>
                <Typography width={88} fontWeight="bold" textAlign="left" mb={1}>
                  Start Date:
                </Typography>
                <FHAntdDateTimePicker
                  data-testid="startDate"
                  format="MM/DD/YYYY"
                  sx={{ boxShadow: 'none' }}
                  allowClear={false}
                  value={startDate ? moment(startDate, 'MM/DD/YY') : ''}
                  disabledDate={disabledFutureDate}
                  style={{ width: '100%' }}
                  colorTheme={Themes.Primary}
                  coloredBackground
                  inputHeight={40}
                  disabled={false}
                  onChange={(e: Moment) => setStartDate(e.format('MM/DD/YY'))}
                  onBlur={() => {}}
                />
              </Grid>
            )}
            {!isBTView && user && (
              <Grid item xs={12} sm={6} md={1.6} sx={{ mr: 2, mb: 1 }}>
                <Typography width={80} fontWeight="bold" textAlign="left" mb={1}>
                  End Date:
                </Typography>
                <FHAntdDateTimePicker
                  data-testid="dateEnd"
                  format="MM/DD/YYYY"
                  allowClear={false}
                  sx={{ boxShadow: 'none' }}
                  value={endDate ? moment(endDate, 'MM/DD/YY') : ''}
                  disabledDate={disabledFutureDate}
                  style={{ width: '100%' }}
                  colorTheme={Themes.Primary}
                  coloredBackground
                  inputHeight={40}
                  disabled={false}
                  onChange={(e: Moment) => setEndDate(e.format('MM/DD/YY'))}
                  onBlur={() => {}}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={2} sx={{ mr: 2, mb: 1 }}>
              <Typography fontWeight={'bold'} mb={1}>
                Goal:
              </Typography>
              {clientGoals && (
                <FHSelect
                  data-testid="data-dashboard-goal"
                  sx={{ width: '100%', boxShadow: 'none', borderRadius: '8px' }}
                  menuProps={{
                    '& .MuiPaper-root': {
                      padding: isBTView ? 0 : 1.2,
                      borderRadius: '10px',
                      mt: isBTView ? 0 : 1,
                      pt: 0.5,
                      '&::-webkit-scrollbar': {
                        backgroundColor: FORTA_WHITE,
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: FORTA_WHITE,
                      },
                    },
                    '& .MuiMenuItem-root': {
                      fontSize: 16,
                      backgroundColor: isBTView ? 'transparent' : FORTA_WHITE,
                      borderRadius: isBTView ? 0 : '8px',
                      mt: 0.5,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: FORTA_WHITE,
                        color: 'inherit',
                      },
                    },
                    maxHeight: '500px',
                  }}
                  backgroundType={BackgroundTypes.Colored}
                  inputHeight={40}
                  fullWidth
                  displayEmpty
                  disabled={clientGoals?.length === 0}
                  options={clientGoals
                    .map((goalOfClient: IDDClientGoals) => ({
                      value: goalOfClient.id,
                      label: (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: 2,
                          }}
                        >
                          <Typography>{capitalizeFirstLetter(goalOfClient.label)}</Typography>
                          <Typography
                            sx={{
                              color: FORTA_BLUE.primary,
                              fontSize: '14px',
                              textDecoration: 'underline',
                              textUnderlineOffset: '2px',
                              textDecorationThickness: '1px',
                              textDecorationColor: 'rgba(0, 0, 0, 0.2)',
                              '.MuiMenuItem-root:hover &, .Mui-selected &': {
                                color: FORTA_WHITE,
                              },
                            }}
                          >
                            {goalOfClient.treatment_plan_id
                              ? formatEntityId(goalOfClient.treatment_plan_id, 'FT')
                              : ''}
                          </Typography>
                        </Box>
                      ),
                    }))
                    .sort((a, b) => {
                      const aLabel = a.label.props.children[0].props.children.toLowerCase();
                      const bLabel = b.label.props.children[0].props.children.toLowerCase();
                      if (aLabel > bLabel) return 1;
                      else if (aLabel < bLabel) return -1;
                      else return 0;
                    })}
                  value={selectedGoal ? selectedGoal?.id : ''}
                  onUpdate={(value: SelectValueType) =>
                    setSelectedGoal(clientGoals.find((goal) => goal.id === value) || null)
                  }
                  renderValue={(value) => {
                    if (!value) return <Typography>Select goal</Typography>;
                    const selectedGoalItem = clientGoals.find((goal) => goal.id === value);
                    return (
                      <Typography>
                        {capitalizeFirstLetter(selectedGoalItem?.label || '')}
                      </Typography>
                    );
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ mr: 2, mb: 1 }}>
              <Typography fontWeight={'bold'} mb={1}>
                Target:
              </Typography>
              {targetOptions && (
                <FMultiSelect
                  data-testid="data-dashboard-targets"
                  sx={{ width: '100%' }}
                  placeholder="Select target"
                  backgroundType={BackgroundTypes.Colored}
                  inputHeight={40}
                  fullWidth
                  value={targetOptions.filter((t) => t.checked).map((t) => t.id as number)}
                  disabled={targetOptions?.length === 0}
                  displayEmpty
                  renderValue={
                    targetOptions.some((t) => t.checked)
                      ? () =>
                          targetOptions
                            .filter((t) => t.checked)
                            .map((t) => t.label)
                            .join(', ')
                      : () => <Typography>Select target</Typography>
                  }
                  onChange={(e: SelectChangeEvent<unknown>) => {
                    const targetIdArray = e.target.value as number[];
                    setTargetOptions(
                      targetOptions.map((target: ITargetOption) => {
                        if (targetIdArray.indexOf(target.id as number) >= 0) {
                          target.checked = true;
                        } else {
                          target.checked = false;
                        }
                        return target;
                      }),
                    );
                  }}
                >
                  {targetOptions.map((target) => (
                    <MenuItem key={target.id} value={target.id}>
                      <FCheckbox
                        checked={target.checked}
                        data-testid={`data-dashboard-target-${target.label}`}
                      />
                      <ListItemText primary={target.label} />
                    </MenuItem>
                  ))}
                </FMultiSelect>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ mt: 4 }}>
              <FHButton
                data-testid="data-dashboard-generate-chart"
                disabled={!isQueryValid || !isQueryChanged}
                onClick={handleGenerateChartButtonClick}
              >
                Generate Chart
              </FHButton>
            </Grid>
          </Grid>
          {dcm === TPGoalDataCollectionMethod.DURATION && selectedTargets.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', width: 360, mt: 3, ml: 1 }}>
              <Typography fontWeight="bold" align="left" width={'200px'}>
                Display chart by:
              </Typography>
              <FHSelect
                sx={{ width: '100%' }}
                inputHeight={40}
                backgroundType={BackgroundTypes.Colored}
                disabled={chartData?.length === 0}
                options={DurationChartPlotOptions}
                optionDataTestIdPrefix="baseLineLevelOption"
                value={durationChartPlotMethod}
                onUpdate={(value) => setDurationChartPlotMethod(value as DurationChartPlotMethod)}
              />
            </Box>
          )}
        </Box>
        {showNoDataAlert && (
          <Typography color={FORTA_ERROR}>No data present for selected Target(s).</Typography>
        )}
        <Grid container spacing={5} flexGrow={1}>
          {selectedTargets && selectedTargets.length > 0 && (
            <Grid
              data-testid="chartArea"
              item
              xs={12}
              md={7}
              sx={{ width: '680px', height: '680px' }}
            >
              <DataChartLegacy
                endDate={chartEndDate}
                durationUnit={durationUnit.toLowerCase()}
                rateUnit={rateUnit.toLowerCase()}
                durationChartPlotMethod={durationChartPlotMethod}
                startDate={chartStartDate}
                filteredPhaseChanges={filteredPhaseChanges}
                selectedTargets={selectedTargets}
                showBaselineDataPoint={
                  trialSummary?.length > 0 &&
                  selectedGoal?.id === trialSummary[0]?.goal_id &&
                  showBaseline
                }
                chartData={chartData}
              />
            </Grid>
          )}
          <Grid item xs={12} md={5}>
            {selectedTargets && selectedTargets.length > 0 && (
              <TargetPhaseChangeTableLegacy
                dcm={dcm}
                durationUnit={durationUnit.toLowerCase()}
                rateUnit={rateUnit.toLowerCase()}
                tableData={tableData}
                tableColumnNames={tableColumnNames}
                phaseChangeTableData={phaseChangeTableData}
                trialAverageTableData={trialAverageTableData}
                durationTrialAverageTableData={durationTrialSummaryTableData}
                selectedTargetsCount={selectedTargets.length}
              />
            )}
          </Grid>
        </Grid>
      </PageContainer>
    </ThemeProvider>
  );
}

export default DataDashboardLegacy;
